/**
 * SKIN-43 / SKIN-206 — the one place that pulls libraw-wasm in at runtime.
 *
 * Kept in its own module for two reasons that happen to coincide:
 *
 * 1. Build. libraw spawns its decoder with `new Worker(new URL('./worker.js',
 *    import.meta.url), { type: 'module' })` and fetches a sibling `.wasm`.
 *    Rollup's worker-import-meta-url plugin deadlocks on that at production
 *    build time, so the package is served as a static asset out of
 *    `public/vendor/libraw-wasm/` and imported by URL, never through the
 *    bundler. `optimizeDeps.exclude: ['libraw-wasm']` in `vite.config.js` and
 *    `.storybook/main.ts` keeps Vite's dep pre-bundling off it too.
 * 2. Test. That same Worker, plus the shared `WebAssembly.Memory` libraw
 *    allocates for pthreads, means it cannot run under jsdom at all. Isolating
 *    the loader gives `clinicalImageDecode.test.ts` a seam to replace, so the
 *    sniff-and-route logic is testable without the wasm.
 */

/** The slice of libraw-wasm's API that the decoder actually uses. */
export interface LibRawImage {
    data: Uint8Array | Uint16Array;
    width: number;
    height: number;
    colors: number;
    bits: number;
}

export interface LibRawInstance {
    open(bytes: Uint8Array, options: { halfSize: boolean; useCameraWb: boolean; outputBps: number }): Promise<void>;
    imageData(): Promise<LibRawImage | null>;
    dispose(): void;
}

export type LibRawConstructor = new () => LibRawInstance;

export async function loadLibRaw(): Promise<LibRawConstructor> {
    // Resolved against document.baseURI so the package's own worker and .wasm
    // resolve relative to /vendor/libraw-wasm/ exactly as it expects.
    const librawUrl = new URL('vendor/libraw-wasm/index.js', document.baseURI).href;
    const { default: LibRaw } = await import(/* @vite-ignore */ librawUrl);
    return LibRaw as LibRawConstructor;
}
